import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth-context";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from "@/components/shared/SmsConsentConfirmation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ToastAction } from "@/components/ui/toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { queryKeys, useCompanySettingsQuery, useProjectScheduleBlocks, useProjects, useScheduleBlocks, useTeamMembers } from "@/hooks/use-app-queries";
import { invalidateProjectData } from "@/lib/query-invalidation";
import { updateProject } from "@/lib/projects-clients";
import {
  createScheduleBlock,
  deleteScheduleBlock,
  fetchScheduleBlocks,
  sendProjectScheduleMessage,
  updateScheduleBlock,
  type ScheduleBlockItem,
  type ScheduleBlockStatus,
} from "@/lib/schedule";
import type { ProjectItem } from "@/lib/projects-clients";
import { cn } from "@/lib/utils";
import {
  Ban,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  Check,
  CircleCheckBig,
  Clock3,
  FolderOpen,
  Info,
  Mail,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plus,
  Sparkles,
  Smartphone,
  Trash2,
} from "lucide-react";
import type { ApprovalDeliveryMethod } from "@/lib/tickets";

type ScheduleProject = {
  id: string;
  name: string;
  clientName: string;
  clientEmail?: string | null;
  clientPhone?: string | null;
  address: string;
  city: string;
  notes: string;
  nextSteps: string;
  status?: ProjectItem["status"];
  approvedTicketCount: number;
  assignedMember?: {
    id: string;
    fullName: string;
    email: string;
    role?: string;
  } | null;
};

type DisplayScheduleBlock = {
  id: string;
  projectId: string;
  startAtIso: string;
  endAtIso: string;
  date: string;
  startMinutes: number;
  endMinutes: number;
  status: ScheduleBlockStatus;
  notes?: string;
  assignedMember?: {
    id: string;
    fullName: string;
    email: string;
    role?: string;
  } | null;
};

type DraftScheduleEntry = {
  id: string;
  existingBlockId?: string | null;
  date: string;
  startTime: string;
  durationValue: string;
  customDurationMinutes: string;
};

const DURATION_OPTIONS = [30, 60, 120, 180, 240] as const;
const ACTIVE_SCHEDULE_STATUSES: ScheduleBlockStatus[] = ["SCHEDULED", "IN_PROGRESS", "PAUSED"];

function toLocalDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(dateInput: string, days: number) {
  const date = new Date(`${dateInput}T00:00:00`);
  date.setDate(date.getDate() + days);
  return toLocalDateInput(date);
}

function getWeekDates(dateInput: string) {
  const date = new Date(`${dateInput}T00:00:00`);
  const day = date.getDay();
  const diffToMonday = (day + 6) % 7;
  date.setDate(date.getDate() - diffToMonday);

  return Array.from({ length: 6 }, (_, index) => {
    const current = new Date(date);
    current.setDate(date.getDate() + index);
    return toLocalDateInput(current);
  });
}

function toLocalTimeInput(date: Date) {
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
}

function minutesToTimeInput(minutes: number) {
  const normalized = Math.max(0, Math.min(23 * 60 + 59, minutes));
  const hours = `${Math.floor(normalized / 60)}`.padStart(2, "0");
  const mins = `${normalized % 60}`.padStart(2, "0");
  return `${hours}:${mins}`;
}

function roundMinutesUp(minutes: number, step = 15) {
  return Math.ceil(minutes / step) * step;
}

function laterTime(left: string, right: string) {
  return left > right ? left : right;
}

function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hours >= 12 ? "PM" : "AM";
  const normalizedHours = hours % 12 === 0 ? 12 : hours % 12;
  if (mins === 0) return `${normalizedHours}:00 ${suffix}`;
  return `${normalizedHours}:${`${mins}`.padStart(2, "0")} ${suffix}`;
}

function formatRange(startMinutes: number, endMinutes: number) {
  return `${formatMinutes(startMinutes)} - ${formatMinutes(endMinutes)}`;
}

function formatHoursAndMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function timeStringToMinutes(value: string | null | undefined, fallback: number) {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return fallback;
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return hours * 60 + minutes;
}

function clampMinutes(value: number, dayStartMinutes: number, dayEndMinutes: number) {
  return Math.min(dayEndMinutes, Math.max(dayStartMinutes, value));
}

function minuteToPreviewPercent(minutes: number, dayStartMinutes: number, dayEndMinutes: number) {
  return (
    ((clampMinutes(minutes, dayStartMinutes, dayEndMinutes) - dayStartMinutes) /
      Math.max(1, dayEndMinutes - dayStartMinutes)) *
    100
  );
}

function formatHourTick(minutes: number) {
  const hour = Math.floor(minutes / 60);
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${normalizedHour}${suffix === "AM" ? "a" : "p"}`;
}

function formatCompactTime(minutes: number) {
  const hour = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  if (mins === 0) return `${normalizedHour}${suffix === "AM" ? "a" : "p"}`;
  return `${normalizedHour}:${`${mins}`.padStart(2, "0")}`;
}

function formatCompactRange(startMinutes: number, endMinutes: number) {
  return `${formatCompactTime(startMinutes)} - ${formatCompactTime(endMinutes)}`;
}

function formatShortDate(dateInput: string) {
  const date = new Date(`${dateInput}T00:00:00`);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
}

function timelineDotClass(status: ScheduleBlockStatus) {
  switch (status) {
    case "DONE":
      return "border-emerald-500 bg-emerald-500";
    case "CANCELED":
    case "MISSED":
      return "border-rose-500 bg-rose-500";
    case "IN_PROGRESS":
      return "border-sky-500 bg-sky-500";
    case "PAUSED":
      return "border-violet-500 bg-violet-500";
    case "SCHEDULED":
    default:
      return "border-amber-500 bg-background";
  }
}

function statusPillClass(status: ScheduleBlockStatus) {
  switch (status) {
    case "DONE":
      return "border-emerald-500/25 bg-emerald-500/10 text-emerald-700";
    case "CANCELED":
    case "MISSED":
      return "border-rose-500/20 bg-rose-500/10 text-rose-700";
    case "IN_PROGRESS":
      return "border-sky-500/20 bg-sky-500/10 text-sky-700";
    case "PAUSED":
      return "border-violet-500/20 bg-violet-500/10 text-violet-700";
    case "SCHEDULED":
    default:
      return "border-amber-500/20 bg-amber-500/10 text-amber-700";
  }
}

function previewBlockClass(status: ScheduleBlockStatus) {
  switch (status) {
    case "DONE":
      return "bg-emerald-500/75 border border-emerald-600/20";
    case "IN_PROGRESS":
      return "bg-sky-500/75 border border-sky-600/20";
    case "PAUSED":
      return "bg-violet-500/75 border border-violet-600/20";
    case "MISSED":
      return "bg-rose-400/65 border border-rose-600/20";
    case "CANCELED":
      return "bg-slate-300/70 border border-slate-400/20";
    case "SCHEDULED":
    default:
      return "bg-amber-400/80 border border-amber-600/20";
  }
}

function getMinutesFromIso(iso: string) {
  const date = new Date(iso);
  return date.getHours() * 60 + date.getMinutes();
}

function isoToLocalDate(iso: string) {
  return toLocalDateInput(new Date(iso));
}

function toProjectView(project: ProjectItem): ScheduleProject {
  return {
    id: project.id,
    name: project.name,
    clientName: project.client?.name ?? "Client",
    clientEmail: project.client?.email ?? null,
    clientPhone: project.client?.phone ?? null,
    address: project.jobSiteAddress?.trim() || "No address added yet",
    city: "",
    notes: `Coordinate onsite access for ${project.name}.`,
    nextSteps: "Confirm crew timing, materials, and site access before the block starts.",
    status: project.status,
    approvedTicketCount: Number(project.approvedTicketCount ?? 0),
    assignedMember: project.assignedMember ?? null,
  };
}

function memberDisplayName(member?: { fullName?: string | null; email?: string | null } | null) {
  return member?.fullName?.trim() || member?.email?.trim() || "";
}

function normalizePhoneForDelivery(value?: string | null) {
  const raw = value?.trim();
  if (!raw) return null;

  if (raw.startsWith("+")) {
    const normalized = `+${raw.slice(1).replace(/\D/g, "")}`;
    return /^\+\d{10,15}$/.test(normalized) ? normalized : null;
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function toDisplayBlock(item: ScheduleBlockItem): DisplayScheduleBlock {
  return {
    id: item.id,
    projectId: item.projectId,
    startAtIso: item.startAt,
    endAtIso: item.endAt,
    date: isoToLocalDate(item.startAt),
    startMinutes: getMinutesFromIso(item.startAt),
    endMinutes: getMinutesFromIso(item.endAt),
    status: item.status,
    notes: item.notes ?? undefined,
    assignedMember: item.assignedMember ?? item.project.assignedMember ?? null,
  };
}

function toIsoFromDateAndTime(date: string, time: string) {
  return new Date(`${date}T${time}:00`).toISOString();
}

function shiftIsoByDays(iso: string, days: number) {
  const date = new Date(iso);
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

function createDraftEntry(date: string, startTime = "09:00"): DraftScheduleEntry {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    existingBlockId: null,
    date,
    startTime,
    durationValue: "120",
    customDurationMinutes: "90",
  };
}

function createDraftEntryFromBlock(block: DisplayScheduleBlock): DraftScheduleEntry {
  const duration = Math.max(0, block.endMinutes - block.startMinutes);
  const presetDuration = DURATION_OPTIONS.find((value) => value === duration);
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    existingBlockId: block.id,
    date: block.date,
    startTime: `${`${Math.floor(block.startMinutes / 60)}`.padStart(2, "0")}:${`${block.startMinutes % 60}`.padStart(2, "0")}`,
    durationValue: presetDuration ? String(presetDuration) : "custom",
    customDurationMinutes: String(duration || 90),
  };
}

export default function Schedule() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => toLocalDateInput(new Date()), []);
  const tomorrow = useMemo(() => addDays(today, 1), [today]);
  const nowTime = useMemo(() => {
    const now = new Date();
    return minutesToTimeInput(roundMinutesUp(now.getHours() * 60 + now.getMinutes(), 15));
  }, []);
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMode, setViewMode] = useState<"day" | "week">("day");
  const [assignedMemberFilter, setAssignedMemberFilter] = useState("all");
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [scheduleAssignedMemberId, setScheduleAssignedMemberId] = useState("unassigned");
  const [draftEntries, setDraftEntries] = useState<DraftScheduleEntry[]>([createDraftEntry(today)]);
  const [activeDraftEntryId, setActiveDraftEntryId] = useState<string | null>(null);
  const [openTimePickerEntryId, setOpenTimePickerEntryId] = useState<string | null>(null);
  const [collapsedWeekDays, setCollapsedWeekDays] = useState<string[]>([]);
  const [scheduleNotes, setScheduleNotes] = useState("");
  const [scheduleDeliveryMethod, setScheduleDeliveryMethod] =
    useState<ApprovalDeliveryMethod>("EMAIL");
  const [scheduleSmsConsentConfirmed, setScheduleSmsConsentConfirmed] =
    useState(false);
  const [scheduleSmsConsentError, setScheduleSmsConsentError] = useState("");
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleHelpOpen, setScheduleHelpOpen] = useState(false);
  const [updatingBlockId, setUpdatingBlockId] = useState<string | null>(null);
  const [cancelTargetBlock, setCancelTargetBlock] = useState<{
    id: string;
    date: string;
    projectName: string;
  } | null>(null);
  const [noteTargetBlock, setNoteTargetBlock] = useState<{
    id: string;
    date: string;
    projectName: string;
    notes: string;
  } | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [inactiveProjectPrompt, setInactiveProjectPrompt] = useState<ScheduleProject | null>(null);
  const [reactivatingProject, setReactivatingProject] = useState(false);

  const activeDraftEntry = useMemo(() => {
    if (draftEntries.length === 0) return null;
    if (!activeDraftEntryId) return draftEntries[0];
    return draftEntries.find((entry) => entry.id === activeDraftEntryId) ?? draftEntries[0];
  }, [activeDraftEntryId, draftEntries]);
  const activeScheduleDate = activeDraftEntry?.date ?? today;

  const projectsQuery = useProjects();
  const companySettingsQuery = useCompanySettingsQuery();
  const blocksQuery = useScheduleBlocks(selectedDate);
  const isTeamPlan = user?.currentPlan === "TEAM";
  const teamMembersQuery = useTeamMembers(isTeamPlan);
  const activeTeamMembers = useMemo(
    () =>
      (teamMembersQuery.data ?? []).filter(
        (member) => member.kind === "user" && member.status === "ACTIVE" && member.isActive,
      ),
    [teamMembersQuery.data],
  );
  const todayBlocksQuery = useScheduleBlocks(today, selectedDate !== today);
  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);
  const weekBlocksQuery = useQuery({
    queryKey: ["schedule-week-blocks", ...weekDates],
    queryFn: async () => {
      const results = await Promise.all(weekDates.map((date) => fetchScheduleBlocks({ date })));
      return results.flatMap((result) => result.items);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    placeholderData: [],
  });
  const scheduleDateBlocksQuery = useScheduleBlocks(
    activeScheduleDate,
    createPanelOpen && activeScheduleDate !== selectedDate,
  );
  const selectedProjectFutureBlocksQuery = useProjectScheduleBlocks(
    selectedProjectId,
    true,
    createPanelOpen && Boolean(selectedProjectId),
  );

  const projects = useMemo(
    () => (projectsQuery.data ?? []).map(toProjectView),
    [projectsQuery.data],
  );
  const schedulableProjects = useMemo(
    () => projects.filter((project) => project.approvedTicketCount > 0),
    [projects],
  );
  const workdayStartMinutes = useMemo(
    () => timeStringToMinutes(companySettingsQuery.data?.scheduleWorkdayStartTime, 8 * 60),
    [companySettingsQuery.data?.scheduleWorkdayStartTime],
  );
  const workdayEndMinutes = useMemo(() => {
    const parsed = timeStringToMinutes(companySettingsQuery.data?.scheduleWorkdayEndTime, 19 * 60);
    return parsed > workdayStartMinutes ? parsed : workdayStartMinutes + 60;
  }, [companySettingsQuery.data?.scheduleWorkdayEndTime, workdayStartMinutes]);
  const workdayStartTime = useMemo(
    () => `${`${Math.floor(workdayStartMinutes / 60)}`.padStart(2, "0")}:${`${workdayStartMinutes % 60}`.padStart(2, "0")}`,
    [workdayStartMinutes],
  );
  const workdayEndTime = useMemo(
    () => `${`${Math.floor(workdayEndMinutes / 60)}`.padStart(2, "0")}:${`${workdayEndMinutes % 60}`.padStart(2, "0")}`,
    [workdayEndMinutes],
  );
  const allTimeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let minute = workdayStartMinutes; minute < workdayEndMinutes; minute += 15) {
      slots.push(minutesToTimeInput(minute));
    }
    return slots;
  }, [workdayEndMinutes, workdayStartMinutes]);
  const getDefaultStartTimeForDate = useCallback(
    (date: string) => (date === today ? laterTime(nowTime, workdayStartTime) : workdayStartTime),
    [nowTime, today, workdayStartTime],
  );
  const minSelectableTime = getDefaultStartTimeForDate(activeScheduleDate);
  const scheduleBlocks = useMemo(
    () => (blocksQuery.data?.items ?? []).map(toDisplayBlock),
    [blocksQuery.data?.items],
  );
  const weekScheduleBlocks = useMemo(
    () => (weekBlocksQuery.data ?? []).map(toDisplayBlock),
    [weekBlocksQuery.data],
  );
  const todayScheduleBlocks = useMemo(
    () =>
      (selectedDate === today ? blocksQuery.data?.items ?? [] : todayBlocksQuery.data?.items ?? []).map(
        toDisplayBlock,
      ),
    [blocksQuery.data?.items, selectedDate, today, todayBlocksQuery.data?.items],
  );

  useEffect(() => {
    const updateViewport = () => {
      if (typeof window === "undefined") return;
      setIsMobileLayout(window.innerWidth < 1024);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 30_000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    setDraftEntries([createDraftEntry(selectedDate, getDefaultStartTimeForDate(selectedDate))]);
    setActiveDraftEntryId(null);
  }, [getDefaultStartTimeForDate, selectedDate]);

  useEffect(() => {
    const targetDate = searchParams.get("date");
    if (targetDate && targetDate !== selectedDate) {
      setSelectedDate(targetDate);
    }

    const preselectProjectId = searchParams.get("projectId");
    if (!preselectProjectId || schedulableProjects.length === 0) return;
    if (!schedulableProjects.some((project) => project.id === preselectProjectId)) return;

    handleProjectSelection(preselectProjectId);
    setCreatePanelOpen(true);
  }, [schedulableProjects, searchParams]);

  useEffect(() => {
    setDraftEntries((current) =>
      current.map((entry) => {
        const nextEntry = { ...entry };

        if (nextEntry.date < today) {
          return {
            ...nextEntry,
            date: today,
            startTime: getDefaultStartTimeForDate(today),
          };
        }

        const entryMinTime = getDefaultStartTimeForDate(nextEntry.date);
        if (nextEntry.startTime < entryMinTime) {
          return {
            ...nextEntry,
            startTime: entryMinTime,
          };
        }

        if (nextEntry.startTime > workdayEndTime) {
          return {
            ...nextEntry,
            startTime: workdayEndTime,
          };
        }

        return nextEntry;
      }),
    );
  }, [getDefaultStartTimeForDate, today, workdayEndTime]);

  const matchesAssignedMemberFilter = useCallback(
    (block: DisplayScheduleBlock) => {
      if (!isTeamPlan || assignedMemberFilter === "all") return true;
      if (assignedMemberFilter === "unassigned") return !block.assignedMember?.id;
      return block.assignedMember?.id === assignedMemberFilter;
    },
    [assignedMemberFilter, isTeamPlan],
  );
  const selectedDayBlocks = useMemo(
    () => [...scheduleBlocks].filter(matchesAssignedMemberFilter).sort((left, right) => left.startMinutes - right.startMinutes),
    [matchesAssignedMemberFilter, scheduleBlocks],
  );
  const selectedWeekBlocks = useMemo(
    () =>
      [...weekScheduleBlocks]
        .filter((block) => weekDates.includes(block.date))
        .filter(matchesAssignedMemberFilter)
        .sort((left, right) => {
          if (left.date !== right.date) return left.date.localeCompare(right.date);
          return left.startMinutes - right.startMinutes;
        }),
    [matchesAssignedMemberFilter, weekDates, weekScheduleBlocks],
  );
  const displayedBlocks = viewMode === "week" ? selectedWeekBlocks : selectedDayBlocks;

  useEffect(() => {
    if (displayedBlocks.length === 0) {
      setSelectedBlockId(null);
      return;
    }
    const targetBlockId = searchParams.get("blockId");
    if (targetBlockId && displayedBlocks.some((block) => block.id === targetBlockId)) {
      setSelectedBlockId(targetBlockId);
      return;
    }
    if (!selectedBlockId || !displayedBlocks.some((block) => block.id === selectedBlockId)) {
      setSelectedBlockId(displayedBlocks[0].id);
    }
  }, [displayedBlocks, searchParams, selectedBlockId]);

  useEffect(() => {
    setCollapsedWeekDays([]);
  }, [selectedDate]);

  const scheduleDateBlocks = useMemo(() => {
    const source =
      activeScheduleDate === selectedDate
        ? blocksQuery.data?.date === selectedDate
          ? blocksQuery.data?.items ?? []
          : []
        : scheduleDateBlocksQuery.data?.date === activeScheduleDate
          ? scheduleDateBlocksQuery.data?.items ?? []
          : [];

    return source.map(toDisplayBlock);
  }, [
    activeScheduleDate,
    blocksQuery.data?.date,
    blocksQuery.data?.items,
    scheduleDateBlocksQuery.data?.date,
    scheduleDateBlocksQuery.data?.items,
    selectedDate,
  ]);
  const selectedProjectFutureBlocks = useMemo(
    () => (selectedProjectFutureBlocksQuery.data?.items ?? []).map(toDisplayBlock),
    [selectedProjectFutureBlocksQuery.data?.items],
  );
  const selectedScheduleProject = useMemo(
    () => schedulableProjects.find((project) => project.id === selectedProjectId) ?? null,
    [schedulableProjects, selectedProjectId],
  );
  const selectedScheduleMember = useMemo(() => {
    if (!isTeamPlan || scheduleAssignedMemberId === "unassigned") return null;
    return (
      activeTeamMembers.find((member) => member.id === scheduleAssignedMemberId) ??
      (selectedScheduleProject?.assignedMember?.id === scheduleAssignedMemberId
        ? selectedScheduleProject.assignedMember
        : null)
    );
  }, [activeTeamMembers, isTeamPlan, scheduleAssignedMemberId, selectedScheduleProject?.assignedMember]);
  const selectedScheduleMemberName = memberDisplayName(selectedScheduleMember);
  const requiresScheduleAssignment = isTeamPlan && scheduleAssignedMemberId === "unassigned";
  const normalizedSchedulePhone = useMemo(
    () => normalizePhoneForDelivery(selectedScheduleProject?.clientPhone),
    [selectedScheduleProject?.clientPhone],
  );
  const smsEnabledByEnv = import.meta.env.VITE_SMS_APPROVALS_ENABLED === "true";
  const smsAllowedByPlan = user?.currentPlan !== "STARTER";
  const scheduleHasEmail = Boolean(selectedScheduleProject?.clientEmail?.trim());
  const scheduleHasPhone = Boolean(normalizedSchedulePhone);
  const canUseScheduleSms = smsEnabledByEnv && smsAllowedByPlan && scheduleHasPhone;
  const scheduleAvailableDeliveryMethods = useMemo<ApprovalDeliveryMethod[]>(() => {
    const methods: ApprovalDeliveryMethod[] = [];
    if (scheduleHasEmail) methods.push("EMAIL");
    if (canUseScheduleSms) methods.push("SMS");
    if (scheduleHasEmail && canUseScheduleSms) methods.push("EMAIL_AND_SMS");
    return methods;
  }, [canUseScheduleSms, scheduleHasEmail]);
  const scheduleSmsReason = !smsEnabledByEnv
    ? "SMS is disabled in this environment."
    : !smsAllowedByPlan
      ? "SMS delivery is available on Pro and Team."
      : !scheduleHasPhone
        ? "A valid client phone number is required for SMS."
        : null;
  const scheduleEmailReason = !scheduleHasEmail
    ? "A client email address is required for email delivery."
    : null;
  const scheduleDeliveryUsesSms =
    scheduleDeliveryMethod === "SMS" || scheduleDeliveryMethod === "EMAIL_AND_SMS";

  const availabilityScheduleBlocks = useMemo(() => {
    if (!isTeamPlan) return scheduleDateBlocks;
    if (scheduleAssignedMemberId === "unassigned") return [];
    return scheduleDateBlocks.filter((block) => block.assignedMember?.id === scheduleAssignedMemberId);
  }, [isTeamPlan, scheduleAssignedMemberId, scheduleDateBlocks]);

  useEffect(() => {
    if (!scheduleAvailableDeliveryMethods.length) return;
    if (scheduleAvailableDeliveryMethods.includes(scheduleDeliveryMethod)) return;
    setScheduleDeliveryMethod(
      scheduleAvailableDeliveryMethods.includes("EMAIL")
        ? "EMAIL"
        : scheduleAvailableDeliveryMethods.includes("SMS")
          ? "SMS"
          : "EMAIL_AND_SMS",
    );
  }, [scheduleAvailableDeliveryMethods, scheduleDeliveryMethod]);

  useEffect(() => {
    if (!scheduleDeliveryUsesSms) {
      setScheduleSmsConsentConfirmed(false);
      setScheduleSmsConsentError("");
    }
  }, [scheduleDeliveryUsesSms]);
  const manualStartPromptedRef = useRef<Set<string>>(new Set());
  const manualCompletePromptedRef = useRef<Set<string>>(new Set());

  const blocksById = useMemo(
    () =>
      new Map(
        displayedBlocks.map((block) => [
          block.id,
          {
            ...block,
            project: projects.find((project) => project.id === block.projectId) ?? null,
          },
        ]),
      ),
    [displayedBlocks, projects],
  );

  const selectedBlock = selectedBlockId ? blocksById.get(selectedBlockId) ?? null : null;
  const selectedDetailProjectFutureBlocksQuery = useProjectScheduleBlocks(
    selectedBlock?.project?.id ?? null,
    true,
    Boolean(selectedBlock?.project?.id),
  );
  const selectedDetailProjectFutureBlocks = useMemo(
    () => (selectedDetailProjectFutureBlocksQuery.data?.items ?? []).map(toDisplayBlock),
    [selectedDetailProjectFutureBlocksQuery.data?.items],
  );
  const selectedBlockUpcomingSteps = useMemo(() => {
    if (!selectedBlock) return [];

    return selectedDetailProjectFutureBlocks.filter(
      (block) =>
        block.id !== selectedBlock.id &&
        new Date(block.startAtIso).getTime() >= new Date(selectedBlock.endAtIso).getTime(),
    );
  }, [selectedBlock, selectedDetailProjectFutureBlocks]);
  const selectedBlockMapQuery = selectedBlock?.project
    ? [selectedBlock.project.address, selectedBlock.project.city].filter(Boolean).join(", ")
    : "";
  const selectedBlockMapEmbedUrl = selectedBlockMapQuery
    ? `https://maps.google.com/maps?q=${encodeURIComponent(selectedBlockMapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`
    : null;
  const selectedBlockMapsUrl = selectedBlockMapQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedBlockMapQuery)}`
    : "#";

  const totalScheduledMinutes = displayedBlocks
    .filter((block) => !["CANCELED", "MISSED"].includes(block.status))
    .reduce((sum, block) => sum + (block.endMinutes - block.startMinutes), 0);

  const nextUp = useMemo(() => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const source = selectedDayBlocks.filter((block) =>
      ACTIVE_SCHEDULE_STATUSES.includes(block.status),
    );
    const candidate =
      selectedDate === today
        ? source.find((block) => block.endMinutes >= currentMinutes) ?? source[0]
        : source[0];
    if (!candidate) return null;
    const project = projects.find((item) => item.id === candidate.projectId);
    return project
      ? {
          timeRange: formatRange(candidate.startMinutes, candidate.endMinutes),
          projectName: project.name,
        }
      : null;
  }, [projects, selectedDate, selectedDayBlocks, today]);
  const weekDayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        weekday: "long",
      }),
    [],
  );
  const weekDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
      }),
    [],
  );
  const weekGroups = useMemo(
    () =>
      weekDates.map((date) => {
        const dateObj = new Date(`${date}T00:00:00`);
        const blocks = selectedWeekBlocks.filter((block) => block.date === date);
        return {
          date,
          label: weekDayFormatter.format(dateObj),
          shortDate: weekDateFormatter.format(dateObj),
          blocks,
        };
      }),
    [selectedWeekBlocks, weekDateFormatter, weekDates, weekDayFormatter],
  );
  const isMainListLoading = viewMode === "week" ? weekBlocksQuery.isLoading : blocksQuery.isLoading;

  const computedDurationMinutes = activeDraftEntry
    ? activeDraftEntry.durationValue === "custom"
      ? Number(activeDraftEntry.customDurationMinutes || 0)
      : Number(activeDraftEntry.durationValue)
    : 0;
  const [startHours, startMinutesValue] = (activeDraftEntry?.startTime ?? "09:00").split(":").map(Number);
  const proposedStartMinutes = (startHours || 0) * 60 + (startMinutesValue || 0);
  const proposedEndMinutes = proposedStartMinutes + Math.max(0, computedDurationMinutes || 0);

  const overlapBlock = useMemo(() => {
    if (requiresScheduleAssignment) return null;
    if (!selectedProjectId || !activeDraftEntry || proposedEndMinutes <= proposedStartMinutes) return null;
    return availabilityScheduleBlocks.find(
      (block) =>
        proposedStartMinutes < block.endMinutes && proposedEndMinutes > block.startMinutes,
    );
  }, [
    activeDraftEntry,
    availabilityScheduleBlocks,
    proposedEndMinutes,
    proposedStartMinutes,
    requiresScheduleAssignment,
    selectedProjectId,
  ]);

  const overlapProject = overlapBlock
    ? projects.find((project) => project.id === overlapBlock.projectId) ?? null
    : null;
  const dayPreviewBlocks = useMemo(
    () =>
      availabilityScheduleBlocks
        .filter((block) => block.status !== "CANCELED")
        .sort((left, right) => left.startMinutes - right.startMinutes),
    [availabilityScheduleBlocks],
  );
  const previewTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let minute = workdayStartMinutes; minute <= workdayEndMinutes; minute += 120) {
      ticks.push(minute);
    }
    if (ticks[ticks.length - 1] !== workdayEndMinutes) ticks.push(workdayEndMinutes);
    return ticks;
  }, [workdayEndMinutes, workdayStartMinutes]);

  const clearSchedulePrefill = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("projectId");
    setSearchParams(next, { replace: true });
  };

  const resetCreatePanelState = () => {
    const preselectProjectId = searchParams.get("projectId");
    const preselectedProject =
      preselectProjectId && schedulableProjects.some((project) => project.id === preselectProjectId)
        ? schedulableProjects.find((project) => project.id === preselectProjectId) ?? null
        : null;
    setSelectedProjectId(preselectedProject?.id ?? null);
    setScheduleAssignedMemberId(preselectedProject?.assignedMember?.id ?? "unassigned");
    setDraftEntries([createDraftEntry(selectedDate, getDefaultStartTimeForDate(selectedDate))]);
    setActiveDraftEntryId(null);
    setScheduleNotes("");
    setScheduleDeliveryMethod("EMAIL");
  };

  const openCreatePanel = (projectId?: string) => {
    resetCreatePanelState();
    if (projectId) {
      const project = schedulableProjects.find((item) => item.id === projectId) ?? null;
      setSelectedProjectId(projectId);
      setScheduleAssignedMemberId(project?.assignedMember?.id ?? "unassigned");
    }
    setCreatePanelOpen(true);
  };

  const handleSelectBlock = (blockId: string) => {
    setSelectedBlockId(blockId);
    if (isMobileLayout) setDetailDrawerOpen(true);
  };

  const refreshScheduleData = async (targetDate = selectedDate) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleBlocks(targetDate) }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      invalidateProjectData(queryClient),
    ]);
  };

  const saveSchedule = async (sendMessage = false) => {
    if (!selectedProjectId || draftEntries.length === 0) return;
    const teamAssignedMemberId =
      isTeamPlan && scheduleAssignedMemberId !== "unassigned" ? scheduleAssignedMemberId : "";

    if (isTeamPlan && !teamAssignedMemberId) {
      toast({
        title: t("schedule.assignmentRequiredTitle", "Assign this project before scheduling"),
        description: t(
          "schedule.assignmentRequiredDescription",
          "Select a team member so availability and conflicts are checked correctly.",
        ),
        variant: "destructive",
      });
      return;
    }

    const normalizedEntries = draftEntries
      .map((entry) => {
        const durationMinutes =
          entry.durationValue === "custom"
            ? Number(entry.customDurationMinutes)
            : Number(entry.durationValue);

        return {
          ...entry,
          durationMinutes,
        };
      })
      .filter((entry) => entry.durationMinutes > 0);

    if (normalizedEntries.length === 0) return;
    const scheduleMessageUsesSms =
      sendMessage &&
      (scheduleDeliveryMethod === "SMS" || scheduleDeliveryMethod === "EMAIL_AND_SMS");
    if (scheduleMessageUsesSms && !scheduleSmsConsentConfirmed) {
      setScheduleSmsConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
      toast({
        title: SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
        variant: "destructive",
      });
      return;
    }

    setSavingSchedule(true);
    try {
      const createdBlocks: ScheduleBlockItem[] = [];
      for (const entry of normalizedEntries) {
        const payload = {
          startAt: toIsoFromDateAndTime(entry.date, entry.startTime),
          durationMinutes: entry.durationMinutes,
          notes: scheduleNotes.trim() || undefined,
          assignedMemberId: isTeamPlan ? teamAssignedMemberId : undefined,
        };
        const created = entry.existingBlockId
          ? await updateScheduleBlock(entry.existingBlockId, payload)
          : await createScheduleBlock({
              projectId: selectedProjectId,
              ...payload,
            });
        createdBlocks.push(created);
      }

      if (sendMessage) {
        const messageResult = await sendProjectScheduleMessage({
          projectId: selectedProjectId,
          blockIds: createdBlocks.map((block) => block.id),
          deliveryMethod: scheduleDeliveryMethod,
          smsConsentConfirmed: scheduleMessageUsesSms ? scheduleSmsConsentConfirmed : undefined,
        });

        const sentLabel =
          messageResult.sentChannels.length === 2
            ? "Email and SMS sent"
            : messageResult.sentChannels[0] === "sms"
              ? "SMS sent"
              : "Email sent";
        const failureLabel = messageResult.failedChannels.length
          ? ` Failed: ${messageResult.failedChannels
              .map((item) => item.channel.toUpperCase())
              .join(", ")}.`
          : "";

        toast({
          title: t("schedule.messageSentTitle", "Schedule saved and message sent"),
          description: `${sentLabel}.${failureLabel}`.trim(),
        });
      }

      const datesToRefresh = [...new Set(normalizedEntries.map((entry) => entry.date))];
      await Promise.all(datesToRefresh.map((date) => refreshScheduleData(date)));
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectScheduleBlocks(selectedProjectId, true),
      });
      const firstDate = normalizedEntries[0]?.date ?? selectedDate;
      const lastCreated = createdBlocks[createdBlocks.length - 1];
      setSelectedDate(firstDate);
      setSelectedBlockId(lastCreated?.id ?? null);
      setCreatePanelOpen(false);
      if (isMobileLayout) setDetailDrawerOpen(true);
      clearSchedulePrefill();
      resetCreatePanelState();
      if (!sendMessage) {
        toast({
          title: t("schedule.createdTitle", "Schedule saved"),
        });
      }
    } catch (error) {
      toast({
        title: sendMessage
          ? t("schedule.messageSendFailedTitle", "Unable to send schedule message")
          : t("schedule.createFailedTitle", "Unable to save schedule"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setSavingSchedule(false);
    }
  };

  const openNativePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  const persistBlockUpdate = async (
    blockId: string,
    payload: Parameters<typeof updateScheduleBlock>[1],
    targetDate = selectedDate,
  ) => {
    setUpdatingBlockId(blockId);
    try {
      await updateScheduleBlock(blockId, payload);
      await refreshScheduleData(targetDate);
    } catch (error) {
      toast({
        title: t("schedule.updateFailedTitle", "Unable to update block"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setUpdatingBlockId(null);
    }
  };

  const deleteBlock = async (blockId: string, targetDate = selectedDate) => {
    setUpdatingBlockId(blockId);
    try {
      await deleteScheduleBlock(blockId);
      await refreshScheduleData(targetDate);
      setSelectedBlockId((current) => (current === blockId ? null : current));
      toast({ title: t("schedule.deleteSuccess", "Block deleted") });
    } catch (error) {
      toast({
        title: t("schedule.deleteFailedTitle", "Unable to delete block"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setUpdatingBlockId(null);
    }
  };

  const updateDraftEntry = (
    entryId: string,
    patch: Partial<Pick<DraftScheduleEntry, "date" | "startTime" | "durationValue" | "customDurationMinutes">>,
  ) => {
    setDraftEntries((current) =>
      current.map((entry) => {
        if (entry.id !== entryId) return entry;
        const next = { ...entry, ...patch };
        if (next.date < today) next.date = today;
        const nextMinTime = getDefaultStartTimeForDate(next.date);
        if (next.startTime < nextMinTime) {
          next.startTime = nextMinTime;
        }
        return next;
      }),
    );
  };

  const addDraftEntry = () => {
    const nextEntry = createDraftEntry(activeScheduleDate, getDefaultStartTimeForDate(activeScheduleDate));
    setDraftEntries((current) => [...current, nextEntry]);
    setActiveDraftEntryId(nextEntry.id);
  };

  const removeDraftEntry = (entryId: string) => {
    setDraftEntries((current) => {
      if (current.length === 1) return current;
      const next = current.filter((entry) => entry.id !== entryId);
      if (activeDraftEntryId === entryId) {
        setActiveDraftEntryId(next[0]?.id ?? null);
      }
      return next;
    });
  };

  const editExistingFutureBlock = (block: DisplayScheduleBlock) => {
    const existingDraft = draftEntries.find((entry) => entry.existingBlockId === block.id);
    if (existingDraft) {
      setActiveDraftEntryId(existingDraft.id);
      return;
    }

    const nextEntry = createDraftEntryFromBlock(block);
    setDraftEntries((current) => [...current, nextEntry]);
    setActiveDraftEntryId(nextEntry.id);
  };

  const deleteExistingFutureBlock = async (block: DisplayScheduleBlock) => {
    await deleteBlock(block.id, block.date);
    setDraftEntries((current) => current.filter((entry) => entry.existingBlockId !== block.id));
    await queryClient.invalidateQueries({
      queryKey: queryKeys.projectScheduleBlocks(block.projectId, true),
    });
  };

  const handleProjectSelection = (projectId: string) => {
    if (!projectId) {
      setSelectedProjectId(null);
      setScheduleAssignedMemberId("unassigned");
      return;
    }

    const project = schedulableProjects.find((item) => item.id === projectId) ?? null;
    if (!project) {
      setSelectedProjectId(null);
      setScheduleAssignedMemberId("unassigned");
      return;
    }

    if (project.status && project.status !== "ACTIVE") {
      setInactiveProjectPrompt(project);
      return;
    }

    setSelectedProjectId(projectId);
    setScheduleAssignedMemberId(project.assignedMember?.id ?? "unassigned");
  };

  const activatePromptedProject = async () => {
    if (!inactiveProjectPrompt) return;

    setReactivatingProject(true);
    try {
      await updateProject(inactiveProjectPrompt.id, { status: "ACTIVE" });
      await invalidateProjectData(queryClient);
      setSelectedProjectId(inactiveProjectPrompt.id);
      setScheduleAssignedMemberId(inactiveProjectPrompt.assignedMember?.id ?? "unassigned");
      setInactiveProjectPrompt(null);
      toast({
        title: t("schedule.activateProjectSuccess", "Project is now active"),
      });
    } catch (error) {
      toast({
        title: t("schedule.activateProjectFailed", "Unable to activate project"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setReactivatingProject(false);
    }
  };

  const openEditPanelForBlock = (block: DisplayScheduleBlock, projectId: string, notes?: string) => {
    const nextEntry = createDraftEntryFromBlock(block);
    const project = schedulableProjects.find((item) => item.id === projectId) ?? null;
    setSelectedProjectId(projectId);
    setScheduleAssignedMemberId(
      block.assignedMember?.id ?? project?.assignedMember?.id ?? "unassigned",
    );
    setDraftEntries([nextEntry]);
    setActiveDraftEntryId(nextEntry.id);
    setScheduleNotes(notes ?? "");
    clearSchedulePrefill();
    setCreatePanelOpen(true);
  };

  const confirmCancelBlock = async () => {
    if (!cancelTargetBlock) return;
    await persistBlockUpdate(
      cancelTargetBlock.id,
      { status: "CANCELED" },
      cancelTargetBlock.date,
    );
    setCancelTargetBlock(null);
  };

  const saveBlockNote = async () => {
    if (!noteTargetBlock) return;
    setUpdatingBlockId(noteTargetBlock.id);
    try {
      await updateScheduleBlock(noteTargetBlock.id, {
        notes: noteDraft.trim() || "",
      });
      await refreshScheduleData(noteTargetBlock.date);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectScheduleBlocks(selectedProjectId || "", true),
      });
      toast({ title: t("schedule.noteSaved", "Note saved") });
      setNoteTargetBlock(null);
      setNoteDraft("");
    } catch (error) {
      toast({
        title: t("schedule.noteSaveFailed", "Unable to save note"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setUpdatingBlockId(null);
    }
  };

  useEffect(() => {
    const autoStartEnabled = companySettingsQuery.data?.scheduleAutoStartEnabled ?? true;
    const autoCompleteEnabled = companySettingsQuery.data?.scheduleAutoCompleteEnabled ?? true;
    if (autoStartEnabled && autoCompleteEnabled) return;

    const now = new Date(currentTime);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const relevantBlocks = todayScheduleBlocks
      .map((block) => ({
        block,
        project: projects.find((project) => project.id === block.projectId) ?? null,
      }))
      .filter(({ project }) => Boolean(project));

    const completionCandidate = !autoCompleteEnabled
      ? relevantBlocks.find(({ block }) =>
          !["DONE", "CANCELED", "MISSED"].includes(block.status) && block.endMinutes <= nowMinutes,
        )
      : null;

    if (completionCandidate) {
      const promptKey = `${completionCandidate.block.id}:${completionCandidate.block.endAtIso}`;
      if (!manualCompletePromptedRef.current.has(promptKey)) {
        manualCompletePromptedRef.current.add(promptKey);
        toast({
          title: t("schedule.manualCompletionPromptTitle", "This block has ended"),
          description: t(
            "schedule.manualCompletionPromptDescription",
            {
              project: completionCandidate.project?.name ?? "Project",
              defaultValue:
                "{{project}} has reached its scheduled end time. Do you want to mark it as Done now?",
            },
          ),
          action: (
            <ToastAction
              altText={t("schedule.manualCompletionAction", "Mark done")}
              onClick={() => {
                void persistBlockUpdate(completionCandidate.block.id, { status: "DONE" }, today);
              }}
            >
              {t("schedule.manualCompletionAction", "Mark done")}
            </ToastAction>
          ),
        });
        return;
      }
    }

    const startCandidate = !autoStartEnabled
      ? relevantBlocks.find(({ block }) =>
          block.status === "SCHEDULED" &&
          block.startMinutes <= nowMinutes &&
          block.endMinutes > nowMinutes,
        )
      : null;

    if (startCandidate) {
      const promptKey = `${startCandidate.block.id}:${startCandidate.block.startAtIso}`;
      if (!manualStartPromptedRef.current.has(promptKey)) {
        manualStartPromptedRef.current.add(promptKey);
        toast({
          title: t("schedule.manualStartPromptTitle", "This block is ready to start"),
          description: t(
            "schedule.manualStartPromptDescription",
            {
              project: startCandidate.project?.name ?? "Project",
              defaultValue:
                "{{project}} has reached its scheduled start time. Do you want to move it to In Progress now?",
            },
          ),
          action: (
            <ToastAction
              altText={t("schedule.manualStartAction", "Start now")}
              onClick={() => {
                void persistBlockUpdate(startCandidate.block.id, { status: "IN_PROGRESS" }, today);
              }}
            >
              {t("schedule.manualStartAction", "Start now")}
            </ToastAction>
          ),
        });
      }
    }
  }, [
    companySettingsQuery.data?.scheduleAutoCompleteEnabled,
    companySettingsQuery.data?.scheduleAutoStartEnabled,
    currentTime,
    projects,
    t,
    toast,
    today,
    todayScheduleBlocks,
  ]);

  const detailPanel = selectedBlock && selectedBlock.project ? (
    <div className="rounded-2xl border border-border/70 bg-card/95 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold text-[#0C1F4F]">
            {selectedBlock.project.name}
          </h2>
          <p className="text-sm text-muted-foreground">
            {selectedBlock.project.clientName}
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-6">
        <div className="p-0">
          <div className="flex items-start gap-3">
            <Clock3 className="mt-0.5 h-4.5 w-4.5 text-accent" />
            <div className="space-y-1">
              <p className="text-base font-semibold text-[#0C1F4F]">
                {formatRange(selectedBlock.startMinutes, selectedBlock.endMinutes)}
              </p>
              <p className="text-sm font-medium text-foreground/85">
                {t("schedule.detail.duration", {
                  value: formatHoursAndMinutes(
                    selectedBlock.endMinutes - selectedBlock.startMinutes,
                  ),
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-0">
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-4.5 w-4.5 text-accent" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {selectedBlock.project.address}
              </p>
              {selectedBlock.project.city ? (
                <p className="text-xs text-muted-foreground">{selectedBlock.project.city}</p>
              ) : null}
            </div>
          </div>
        </div>

        {isTeamPlan ? (
          <div className="p-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Assigned to
            </p>
            <p className="mt-1 text-sm font-medium text-foreground">
              {selectedBlock.assignedMember?.fullName || selectedBlock.assignedMember?.email || "Unassigned"}
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          {selectedBlock.notes ? (
            <>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("schedule.detail.notes")}
              </p>
              <p className="text-sm leading-6 text-foreground">{selectedBlock.notes}</p>
            </>
          ) : null}
        </div>

        {selectedBlockUpcomingSteps.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("schedule.detail.nextSteps")}
            </p>
            <div className="space-y-2">
              {selectedBlockUpcomingSteps.map((block) => (
                <div
                  key={block.id}
                  className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2"
                >
                  <p className="text-sm font-medium text-foreground">
                    {formatShortDate(block.date)} · {formatRange(block.startMinutes, block.endMinutes)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatHoursAndMinutes(block.endMinutes - block.startMinutes)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {selectedBlockMapEmbedUrl ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("schedule.detail.locationMap", "Location")}
            </p>
            <div className="overflow-hidden rounded-2xl border border-border/70 bg-muted/20">
              <iframe
                title={`${selectedBlock.project.name} map`}
                src={selectedBlockMapEmbedUrl}
                className="h-[220px] w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-6 grid gap-2 sm:grid-cols-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/projects/${selectedBlock.project.id}`)}
        >
          {t("schedule.actions.openProject")}
        </Button>
        <Button
          variant="outline"
          disabled={updatingBlockId === selectedBlock.id}
          onClick={() =>
            openEditPanelForBlock(
              selectedBlock,
              selectedBlock.project.id,
              selectedBlock.notes,
            )
          }
        >
          {t("schedule.actions.edit")}
        </Button>
        <Button
          variant="outline"
          className="border-rose-500/20 text-rose-700 hover:bg-rose-500/10 hover:text-rose-700"
          disabled={updatingBlockId === selectedBlock.id}
          onClick={() =>
            setCancelTargetBlock({
              id: selectedBlock.id,
              date: selectedBlock.date,
              projectName: selectedBlock.project?.name ?? "project",
            })
          }
        >
          {t("schedule.detail.cancelBlock")}
        </Button>
      </div>
    </div>
  ) : (
    <div className="rounded-2xl border border-dashed border-border/80 bg-card/60 p-6 text-center">
      <Sparkles className="mx-auto h-8 w-8 text-accent/70" />
      <h2 className="mt-4 text-lg font-semibold text-[#0C1F4F]">
        {t("schedule.detail.emptyTitle")}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("schedule.detail.emptyDescription")}
      </p>
    </div>
  );

  const renderScheduleBlockRow = (block: DisplayScheduleBlock) => {
    const project = projects.find((item) => item.id === block.projectId);
    if (!project) return null;
    const isSelected = block.id === selectedBlockId;
    const isPastBlock = new Date(block.endAtIso).getTime() < Date.now();

    return (
      <div key={block.id} className="relative">
        <div
          className={cn(
            "absolute left-[-27px] top-6 z-10 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 shadow-sm",
            timelineDotClass(block.status),
          )}
        />
        <div
          className={cn(
            "group rounded-3xl border border-transparent bg-transparent p-4 shadow-none transition-all duration-200",
            ["CANCELED", "MISSED"].includes(block.status) && "opacity-80",
            !isSelected && "hover:border-[#E7ECF5] hover:bg-white hover:shadow-[0_8px_24px_rgba(12,31,79,0.04)]",
            isSelected && "border-[#E7ECF5] bg-white shadow-[0_12px_30px_rgba(12,31,79,0.08)]",
          )}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => handleSelectBlock(block.id)}
            >
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center rounded-2xl px-0 py-0 text-base font-semibold text-[#0A1A44]">
                  <Clock3 className="mr-1.5 h-3.5 w-3.5" />
                  {formatRange(block.startMinutes, block.endMinutes)}
                </div>
                <div
                  className={cn(
                    "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]",
                    statusPillClass(block.status),
                  )}
                >
                  {t(
                    `schedule.status.${block.status.toLowerCase()}`,
                    block.status.replace("_", " "),
                  )}
                </div>
              </div>
              <p
                className={cn(
                  "mt-3 text-base font-semibold text-[#0A1A44]",
                  block.status === "DONE" && "text-[#0A1A44]/70 line-through",
                )}
              >
                {project.name}
              </p>
              <p className="mt-1 text-sm text-slate-700">{project.address}</p>
              {isTeamPlan ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Assigned to: {block.assignedMember?.fullName || block.assignedMember?.email || "Unassigned"}
                </p>
              ) : null}
              {project.city ? (
                <p className="mt-1 text-xs text-slate-500">{project.city}</p>
              ) : null}
            </button>

            <div className="flex items-center gap-2 self-start">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "rounded-full opacity-0 transition-opacity duration-150 group-hover:opacity-100",
                      isSelected && "opacity-100",
                    )}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                    <FolderOpen className="mr-2 h-4 w-4 text-[#0C1F4F]" />
                    {t("schedule.actions.openProject")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={updatingBlockId === block.id || block.status === "DONE"}
                    onClick={() =>
                      void persistBlockUpdate(block.id, {
                        status: "DONE",
                      })
                    }
                  >
                    <CircleCheckBig className="mr-2 h-4 w-4 text-emerald-600" />
                    {t("schedule.actions.markDone")}
                  </DropdownMenuItem>
                  {!isPastBlock ? (
                    <DropdownMenuItem
                      disabled={updatingBlockId === block.id || block.status === "IN_PROGRESS"}
                      onClick={() => void persistBlockUpdate(block.id, { status: "IN_PROGRESS" })}
                    >
                      <PlayCircle className="mr-2 h-4 w-4 text-sky-600" />
                      {t("schedule.status.in_progress", "In progress")}
                    </DropdownMenuItem>
                  ) : null}
                  {!isPastBlock ? (
                    <DropdownMenuItem
                      disabled={updatingBlockId === block.id || block.status === "PAUSED"}
                      onClick={() => void persistBlockUpdate(block.id, { status: "PAUSED" })}
                    >
                      <PauseCircle className="mr-2 h-4 w-4 text-orange-600" />
                      {t("schedule.status.paused", "Paused")}
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem
                    disabled={updatingBlockId === block.id || block.status === "CANCELED"}
                    onClick={() => void persistBlockUpdate(block.id, { status: "CANCELED" })}
                  >
                    <Ban className="mr-2 h-4 w-4 text-rose-600" />
                    {t("schedule.status.canceled", "Canceled")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {isPastBlock ? (
                    <DropdownMenuItem
                      onClick={() => {
                        setNoteTargetBlock({
                          id: block.id,
                          date: block.date,
                          projectName: project.name,
                          notes: block.notes ?? "",
                        });
                        setNoteDraft(block.notes ?? "");
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4 text-[#0C1F4F]" />
                      {t("schedule.actions.addNote", "Add note")}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => {
                        handleSelectBlock(block.id);
                        openEditPanelForBlock(block, project.id, block.notes);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4 text-[#0C1F4F]" />
                      {t("schedule.actions.edit")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    disabled={updatingBlockId === block.id}
                    className="text-destructive focus:text-destructive"
                    onClick={() => void deleteBlock(block.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4 text-destructive" />
                    {t("schedule.actions.deleteBlock", "Delete block")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>{t("schedule.title")}</span>
            <Popover open={scheduleHelpOpen} onOpenChange={setScheduleHelpOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                  aria-label={t("schedule.help.ariaLabel")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[360px] border-border/80 bg-white p-4 shadow-lg"
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0C1F4F]">{t("schedule.help.title")}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{t("schedule.help.subtitle")}</p>
                  </div>
                  <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                    <li>{t("schedule.help.items.blocks")}</li>
                    <li>{t("schedule.help.items.workflow")}</li>
                    <li>{t("schedule.help.items.multiple")}</li>
                    <li>{t("schedule.help.items.status")}</li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        actions={
          <Button
            className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
            onClick={() => openCreatePanel()}
          >
            <Plus className="h-4 w-4" />
            {t("schedule.actions.scheduleProject")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border border-border/70 bg-card shadow-sm">
          <Button
            type="button"
            variant="ghost"
            className={cn(
              "rounded-r-none",
              selectedDate === today && "bg-accent/10 text-accent",
            )}
            onClick={() => setSelectedDate(today)}
          >
            {t("schedule.todayShortcut")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={cn(
              "rounded-none border-x border-border/70",
              selectedDate === tomorrow && "bg-accent/10 text-accent",
            )}
            onClick={() => setSelectedDate(tomorrow)}
          >
            {t("schedule.tomorrowShortcut")}
          </Button>
          <div className="relative">
            <Input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              onClick={(event) => openNativePicker(event.currentTarget)}
              className="h-10 w-[168px] rounded-l-none border-0 bg-transparent pr-10 shadow-none [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0"
            />
            <button
              type="button"
              className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-accent transition-colors hover:bg-accent/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
              onClick={(event) => {
                const input = event.currentTarget.parentElement?.querySelector("input");
                openNativePicker(input as HTMLInputElement | null);
              }}
              aria-label={t("schedule.modal.date")}
            >
              <CalendarRange className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="inline-flex rounded-xl border border-border/70 bg-card p-1 shadow-sm">
          <Button
            variant={viewMode === "day" ? "secondary" : "ghost"}
            className="h-8 rounded-lg px-3"
            onClick={() => setViewMode("day")}
          >
            {t("schedule.view.day")}
          </Button>
          <Button
            variant={viewMode === "week" ? "secondary" : "ghost"}
            className="h-8 rounded-lg px-3"
            onClick={() => setViewMode("week")}
          >
            {t("schedule.view.week")}
          </Button>
        </div>

        {isTeamPlan ? (
          <div className="flex items-center gap-2 rounded-xl border border-border/70 bg-card px-3 py-2 shadow-sm">
            <Label className="text-xs font-semibold text-muted-foreground">Assigned to</Label>
            <Select value={assignedMemberFilter} onValueChange={setAssignedMemberFilter}>
              <SelectTrigger className="h-8 w-[190px] border-0 bg-transparent px-1 shadow-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {(teamMembersQuery.data ?? [])
                  .filter((member) => member.kind === "user" && member.status === "ACTIVE" && member.isActive)
                  .map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.fullName || member.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t("schedule.summary.totalBlocks")}
          </p>
          <p className="mt-3 text-3xl font-semibold text-[#0C1F4F]">
            {displayedBlocks.length}
          </p>
        </div>
        <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t("schedule.summary.totalHours")}
          </p>
          <p className="mt-3 text-3xl font-semibold text-[#0C1F4F]">
            {formatHoursAndMinutes(totalScheduledMinutes)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("schedule.summary.totalHoursHint")}
          </p>
        </div>
        <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t("schedule.summary.nextUp")}
          </p>
          <div className="mt-3 text-lg font-semibold text-[#0C1F4F]">
            {nextUp ? (
              <p>
                <span className="font-semibold text-amber-600">{nextUp.timeRange}</span>
                <span className="text-[#0C1F4F]"> {" - "}{nextUp.projectName}</span>
              </p>
            ) : (
              <p>{t("schedule.summary.noUpcoming")}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.35fr_0.95fr]">
        <div className="space-y-4">
          <div className="p-0 md:p-0">
            {isMainListLoading ? (
              <div className="rounded-3xl border border-dashed border-border/80 bg-gradient-to-br from-muted/20 to-transparent px-6 py-12 text-center text-sm text-muted-foreground">
                {t("common.loading")}
              </div>
            ) : displayedBlocks.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border/80 bg-gradient-to-br from-muted/20 to-transparent px-6 py-12 text-center">
                <CalendarRange className="mx-auto h-10 w-10 text-accent/70" />
                <h3 className="mt-4 text-lg font-semibold text-[#0C1F4F]">
                  {t("schedule.empty.title")}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("schedule.empty.description")}
                </p>
                <Button
                  className="mt-5 gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
                  onClick={() => openCreatePanel()}
                >
                  <Plus className="h-4 w-4" />
                  {t("schedule.empty.action")}
                </Button>
              </div>
            ) : viewMode === "week" ? (
              <div className="space-y-4">
                {weekGroups.map((group) => {
                  const isCollapsed = collapsedWeekDays.includes(group.date);

                  return (
                    <div
                      key={group.date}
                      className="overflow-hidden rounded-3xl border border-border/70 bg-card/95 shadow-sm"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                        onClick={() =>
                          setCollapsedWeekDays((current) =>
                            current.includes(group.date)
                              ? current.filter((value) => value !== group.date)
                              : [...current, group.date],
                          )
                        }
                      >
                        <div>
                          <p className="text-lg font-semibold text-[#0C1F4F]">{group.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {group.shortDate} •{" "}
                            {t("schedule.weekGroupCount", {
                              count: group.blocks.length,
                              defaultValue:
                                group.blocks.length === 1 ? "1 block" : "{{count}} blocks",
                            })}
                          </p>
                        </div>
                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background">
                          {isCollapsed ? (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </button>

                      {!isCollapsed ? (
                        group.blocks.length > 0 ? (
                          <div className="relative space-y-3 px-5 pb-5 pl-[52px]">
                            <div className="absolute bottom-5 left-[24px] top-1 w-px -translate-x-1/2 bg-border/80" />
                            {group.blocks.map((block) => renderScheduleBlockRow(block))}
                          </div>
                        ) : (
                          <div className="px-5 pb-5 text-sm text-muted-foreground">
                            {t("schedule.weekEmptyDay", "No blocks scheduled for this day.")}
                          </div>
                        )
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="relative space-y-3 pl-[46px]">
                <div className="absolute bottom-3 left-[19px] top-1 w-px -translate-x-1/2 bg-border/80" />
                {selectedDayBlocks.map((block) => renderScheduleBlockRow(block))}
              </div>
            )}
          </div>
        </div>

        <div className="hidden lg:block lg:pt-[10px]">
          <div className="sticky top-[76px]">{detailPanel}</div>
        </div>
      </div>

      <Sheet open={detailDrawerOpen} onOpenChange={setDetailDrawerOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[88vh] overflow-y-auto rounded-t-[28px] px-4"
        >
          <SheetHeader className="px-1 text-left">
            <SheetTitle>
              {selectedBlock?.project?.name || t("schedule.detail.emptyTitle")}
            </SheetTitle>
            <SheetDescription>{t("schedule.mobileDetailDescription")}</SheetDescription>
          </SheetHeader>
          <div className="mt-4">{detailPanel}</div>
          <SheetFooter className="pb-6" />
        </SheetContent>
      </Sheet>

      <Sheet
        open={createPanelOpen}
        onOpenChange={(open) => {
          setCreatePanelOpen(open);
          if (!open) {
            clearSchedulePrefill();
            resetCreatePanelState();
          }
        }}
      >
        <SheetContent
          side={isMobileLayout ? "bottom" : "right"}
          className={cn(
            "overflow-y-auto px-0",
            isMobileLayout ? "max-h-[92vh] rounded-t-[28px]" : "w-full sm:max-w-2xl",
          )}
        >
          <SheetHeader className="px-6 text-left">
            <SheetTitle>{t("schedule.modal.title")}</SheetTitle>
            <SheetDescription>{t("schedule.modal.description")}</SheetDescription>
          </SheetHeader>

          <div className="space-y-4 px-6 pb-6 pt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("schedule.modal.selectProject")}
                </label>
                <select
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={selectedProjectId ?? ""}
                  onChange={(event) => handleProjectSelection(event.target.value)}
                >
                  <option value="">{t("schedule.modal.selectProject")}</option>
                  {schedulableProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} - {project.clientName}
                    </option>
                  ))}
                </select>
                {selectedProjectId ? (
                  <p className="text-xs text-muted-foreground">
                    {schedulableProjects.find((project) => project.id === selectedProjectId)?.address}
                  </p>
                ) : null}
              </div>

              {isTeamPlan && selectedScheduleProject ? (
                <div className="rounded-xl border border-border/70 bg-muted/20 p-3">
                  {selectedScheduleProject.assignedMember?.id ? (
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("schedule.modal.availabilityFor", "Availability for")}
                      </p>
                      <p className="text-sm font-semibold text-[#0C1F4F]">
                        {selectedScheduleMemberName ||
                          memberDisplayName(selectedScheduleProject.assignedMember)}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-sm font-medium text-foreground">
                          {t("schedule.modal.assignedTo", "Assigned to")}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "schedule.modal.assignmentRequired",
                            "This project is unassigned. Assign it to a team member before scheduling.",
                          )}
                        </p>
                      </div>
                      <Select
                        value={scheduleAssignedMemberId}
                        onValueChange={setScheduleAssignedMemberId}
                        disabled={savingSchedule}
                      >
                        <SelectTrigger className="h-10 bg-background">
                          <SelectValue placeholder={t("schedule.modal.selectTeamMember", "Select a team member")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">
                            {t("projects.unassigned", "Unassigned")}
                          </SelectItem>
                          {activeTeamMembers.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {memberDisplayName(member) + ` - ${member.role}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!teamMembersQuery.isLoading && activeTeamMembers.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t("projects.noActiveTeamMembers", "No active team members available.")}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : null}

              {selectedProjectId && selectedProjectFutureBlocks.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm font-medium text-foreground">
                      {t("schedule.modal.existingBlocks", "Existing upcoming blocks")}
                    </label>
                    {selectedProjectFutureBlocks.length > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {t("schedule.modal.existingBlocksCount", {
                          count: selectedProjectFutureBlocks.length,
                          defaultValue: "{{count}} upcoming",
                        })}
                      </p>
                    ) : null}
                  </div>

                  {selectedProjectFutureBlocksQuery.isLoading ? (
                    <div className="rounded-xl border border-dashed border-border/70 px-3 py-3 text-sm text-muted-foreground">
                      {t("common.loading")}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedProjectFutureBlocks.map((block) => {
                        const durationLabel = formatHoursAndMinutes(
                          block.endMinutes - block.startMinutes,
                        );

                        return (
                          <div
                            key={block.id}
                            className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-card/70 px-3 py-3"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">
                                {formatShortDate(block.date)} · {formatRange(block.startMinutes, block.endMinutes)}
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {durationLabel}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => editExistingFutureBlock(block)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                disabled={updatingBlockId === block.id}
                                onClick={() => void deleteExistingFutureBlock(block)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-foreground">
                    {t("schedule.modal.timeBlocks", "Time blocks")}
                  </label>
                  <Button type="button" variant="ghost" className="h-8 px-2 text-accent" onClick={addDraftEntry}>
                    <Plus className="mr-1 h-4 w-4" />
                    {t("schedule.modal.addMoreTime", "Add more time")}
                  </Button>
                </div>

                {draftEntries.map((entry, index) => {
                  const entryMinSelectableTime =
                    entry.date === today
                      ? nowTime > workdayStartTime
                        ? nowTime
                        : workdayStartTime
                      : workdayStartTime;
                  const entryDurationMinutes =
                    entry.durationValue === "custom"
                      ? Number(entry.customDurationMinutes || 0)
                      : Number(entry.durationValue || 0);
                  const availableTimeSlots = allTimeSlots.filter((slot) => {
                    if (requiresScheduleAssignment) return false;
                    if (slot < entryMinSelectableTime || slot > workdayEndTime) return false;
                    if (!entryDurationMinutes || entryDurationMinutes <= 0) return false;

                    const slotStartMinutes = timeStringToMinutes(slot, workdayStartMinutes);
                    const slotEndMinutes = slotStartMinutes + entryDurationMinutes;
                    if (slotEndMinutes > workdayEndMinutes) return false;

                    return !availabilityScheduleBlocks.some((block) => {
                      if (block.date !== entry.date) return false;
                      if (!ACTIVE_SCHEDULE_STATUSES.includes(block.status)) return false;
                      if (entry.existingBlockId && block.id === entry.existingBlockId) return false;
                      return slotStartMinutes < block.endMinutes && slotEndMinutes > block.startMinutes;
                    });
                  });
                  const isActiveEntry = (activeDraftEntry?.id ?? draftEntries[0]?.id) === entry.id;

                  return (
                    <div
                      key={entry.id}
                      className={cn(
                        "space-y-3 rounded-2xl border px-3 py-3 transition-colors",
                        isActiveEntry ? "border-accent/30 bg-accent/5" : "border-border/60 bg-background/70",
                      )}
                      onClick={() => setActiveDraftEntryId(entry.id)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            {t("schedule.modal.blockLabel", { defaultValue: "Block {{index}}", index: index + 1 })}
                          </p>
                          {entry.existingBlockId ? (
                            <span className="rounded-full border border-accent/20 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
                              {t("schedule.modal.editingExisting", "Editing existing")}
                            </span>
                          ) : null}
                        </div>
                        {draftEntries.length > 1 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-7 px-2 text-muted-foreground hover:text-destructive"
                            onClick={(event) => {
                              event.stopPropagation();
                              removeDraftEntry(entry.id);
                            }}
                          >
                            {t("schedule.actions.deleteBlock", "Delete block")}
                          </Button>
                        ) : null}
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            {t("schedule.modal.date")}
                          </label>
                          <div className="relative">
                            <Input
                              type="date"
                              min={today}
                              value={entry.date}
                              onChange={(event) => updateDraftEntry(entry.id, { date: event.target.value })}
                              onClick={(event) => openNativePicker(event.currentTarget)}
                              className="pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:pointer-events-none"
                            />
                            <button
                              type="button"
                              className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-accent transition-colors hover:bg-accent/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
                              onClick={(event) => {
                                const input = event.currentTarget.parentElement?.querySelector("input");
                                openNativePicker(input as HTMLInputElement | null);
                              }}
                              aria-label={t("schedule.modal.date")}
                            >
                              <CalendarRange className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            {t("schedule.modal.startTime")}
                          </label>
                          <Popover
                            open={openTimePickerEntryId === entry.id}
                            onOpenChange={(open) =>
                              setOpenTimePickerEntryId(open ? entry.id : null)
                            }
                          >
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm text-left"
                              >
                                <span className="font-medium text-foreground">
                                  {formatMinutes(
                                    (() => {
                                      const [hours, minutes] = entry.startTime.split(":").map(Number);
                                      return (hours || 0) * 60 + (minutes || 0);
                                    })(),
                                  )}
                                </span>
                                <Clock3 className="h-4 w-4 text-accent" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[240px] p-0" align="start">
                              <div className="border-b px-3 py-2">
                                <p className="text-sm font-semibold text-foreground">
                                  {t("schedule.modal.startTime")}
                                </p>
                              </div>
                              <ScrollArea className="h-64">
                                <div className="p-1">
                                  {availableTimeSlots.length > 0 ? (
                                    availableTimeSlots.map((slot) => (
                                      <button
                                        key={`${entry.id}-${slot}`}
                                        type="button"
                                        className={cn(
                                          "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent/10",
                                          entry.startTime === slot && "bg-accent/10 text-accent",
                                        )}
                                        onClick={() => {
                                          updateDraftEntry(entry.id, { startTime: slot });
                                          setOpenTimePickerEntryId(null);
                                        }}
                                      >
                                        <span>{formatMinutes(timeStringToMinutes(slot, workdayStartMinutes))}</span>
                                        {entry.startTime === slot ? (
                                          <Check className="h-4 w-4 text-accent" />
                                        ) : null}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-3 py-4 text-sm text-muted-foreground">
                                      {t(
                                        "schedule.modal.noAvailableTimes",
                                        "No available time slots for this day.",
                                      )}
                                    </div>
                                  )}
                                </div>
                              </ScrollArea>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            {t("schedule.modal.duration")}
                          </label>
                          <select
                            className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                            value={entry.durationValue}
                            onChange={(event) =>
                              updateDraftEntry(entry.id, { durationValue: event.target.value })
                            }
                          >
                            {DURATION_OPTIONS.map((minutes) => (
                              <option key={minutes} value={minutes}>
                                {minutes === 30 ? "30m" : `${minutes / 60}h`}
                              </option>
                            ))}
                            <option value="custom">{t("schedule.modal.customDuration")}</option>
                          </select>
                        </div>
                      </div>

                      {entry.durationValue === "custom" ? (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            {t("schedule.modal.customMinutes")}
                          </label>
                          <Input
                            type="number"
                            min="15"
                            step="15"
                            value={entry.customDurationMinutes}
                            onChange={(event) =>
                              updateDraftEntry(entry.id, { customDurationMinutes: event.target.value })
                            }
                            placeholder="90"
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <label className="text-sm font-medium text-foreground">
                      {t("schedule.modal.dayPreview", "Day availability")}
                    </label>
                    {isTeamPlan ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {selectedScheduleMemberName
                          ? t("schedule.modal.availabilityForMember", {
                              member: selectedScheduleMemberName,
                              defaultValue: "Availability for: {{member}}",
                            })
                          : t(
                              "schedule.modal.selectMemberForAvailability",
                              "Select a team member to see availability.",
                            )}
                      </p>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {availabilityScheduleBlocks.length > 0
                      ? t("schedule.modal.blocksPlanned", {
                          count: availabilityScheduleBlocks.length,
                          defaultValue: "{{count}} blocks planned",
                        })
                      : t("schedule.modal.dayLooksOpen", "Open day")}
                  </p>
                </div>
                <div className="rounded-2xl border border-border/60 bg-muted/20 px-3 py-3">
                  <div className="relative h-12 overflow-hidden rounded-xl bg-white/80">
                    <div className="absolute inset-0">
                      {previewTicks.map((tick, index) => (
                        <div
                          key={tick}
                          className="absolute bottom-0 top-0 w-px bg-border/50"
                          style={{
                            left: `${minuteToPreviewPercent(tick, workdayStartMinutes, workdayEndMinutes)}%`,
                            transform:
                              index === 0
                                ? "translateX(0)"
                                : index === previewTicks.length - 1
                                  ? "translateX(-1px)"
                                  : "translateX(-0.5px)",
                          }}
                        />
                      ))}
                    </div>
                    {dayPreviewBlocks.map((block) => {
                      const start = clampMinutes(block.startMinutes, workdayStartMinutes, workdayEndMinutes);
                      const end = clampMinutes(block.endMinutes, workdayStartMinutes, workdayEndMinutes);
                      const width = Math.max(
                        2,
                        minuteToPreviewPercent(end, workdayStartMinutes, workdayEndMinutes) -
                          minuteToPreviewPercent(start, workdayStartMinutes, workdayEndMinutes),
                      );
                      const left = minuteToPreviewPercent(start, workdayStartMinutes, workdayEndMinutes);
                      const project = projects.find((item) => item.id === block.projectId);
                      const blockLabel = formatCompactRange(block.startMinutes, block.endMinutes);

                      return (
                        <Tooltip key={block.id}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "absolute top-2 h-8 overflow-hidden rounded-lg px-2 shadow-sm transition-transform hover:scale-[1.01] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
                                previewBlockClass(block.status),
                              )}
                              style={{
                                left: `${left}%`,
                                width: `${width}%`,
                              }}
                              aria-label={`${project?.name ?? "Project"} ${formatRange(block.startMinutes, block.endMinutes)}`}
                            >
                              <span className="flex h-full items-center justify-center truncate text-[10px] font-semibold text-slate-950/85">
                                {blockLabel}
                              </span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            align="center"
                            sideOffset={10}
                            className="max-w-[260px] rounded-xl border border-border/70 bg-popover/95 px-3 py-2 shadow-lg backdrop-blur"
                          >
                            <div className="space-y-1.5">
                              <p className="text-sm font-semibold">
                                {project?.name ?? "Project"}
                              </p>
                              {project?.clientName ? (
                                <p className="text-xs text-muted-foreground">
                                  {project.clientName}
                                </p>
                              ) : null}
                              <p className="text-xs text-muted-foreground">
                                {formatRange(block.startMinutes, block.endMinutes)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatHoursAndMinutes(block.endMinutes - block.startMinutes)}
                              </p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                    {selectedProjectId && proposedEndMinutes > proposedStartMinutes ? (
                      <div
                        className="absolute top-1.5 h-9 rounded-lg border border-accent/40 bg-accent/15 ring-1 ring-accent/15"
                        style={{
                          left: `${minuteToPreviewPercent(proposedStartMinutes, workdayStartMinutes, workdayEndMinutes)}%`,
                          width: `${Math.max(
                            2,
                            minuteToPreviewPercent(proposedEndMinutes, workdayStartMinutes, workdayEndMinutes) -
                              minuteToPreviewPercent(proposedStartMinutes, workdayStartMinutes, workdayEndMinutes),
                          )}%`,
                        }}
                      />
                    ) : null}
                  </div>
                  <div className="relative mt-2 h-4 text-[11px] text-muted-foreground">
                    {previewTicks.map((tick, index) => (
                      <span
                        key={tick}
                        className="absolute top-0 whitespace-nowrap"
                        style={{
                          left: `${minuteToPreviewPercent(tick, workdayStartMinutes, workdayEndMinutes)}%`,
                          transform:
                            index === 0
                              ? "translateX(0)"
                              : index === previewTicks.length - 1
                                ? "translateX(-100%)"
                                : "translateX(-50%)",
                        }}
                      >
                        {formatHourTick(tick)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  "overflow-hidden transition-all duration-300 ease-out",
                  overlapBlock && overlapProject
                    ? "max-h-20 translate-y-0 opacity-100"
                    : "pointer-events-none max-h-0 -translate-y-1 opacity-0",
                )}
              >
                <div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-800">
                  {overlapBlock && overlapProject
                    ? selectedScheduleMemberName
                      ? t("schedule.modal.overlapWarningForMember", {
                          project: overlapProject.name,
                          time: formatRange(overlapBlock.startMinutes, overlapBlock.endMinutes),
                          member: selectedScheduleMemberName,
                          defaultValue: "Overlaps with {{project}} {{time}} for {{member}}",
                        })
                      : t("schedule.modal.overlapWarning", {
                          project: overlapProject.name,
                          time: formatRange(overlapBlock.startMinutes, overlapBlock.endMinutes),
                        })
                    : ""}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("schedule.modal.notes")}
                </label>
                <Textarea
                  value={scheduleNotes}
                  onChange={(event) => setScheduleNotes(event.target.value)}
                  placeholder={t("schedule.modal.notesPlaceholder")}
                  className="min-h-[92px]"
                />
              </div>

              <div className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                <div className="space-y-1">
                  <Label>{t("ticket.deliveryMethod", "Delivery method")}</Label>
                  <p className="text-xs text-muted-foreground">
                    Choose how the schedule confirmation should be sent when you save and send.
                  </p>
                </div>

                <div className="rounded-md border border-border bg-background/80 p-3 text-sm">
                  <div className="font-medium text-card-foreground">
                    {selectedScheduleProject?.clientName || "Client"}
                  </div>
                  <div className="mt-1 space-y-1 text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5" />
                      <span>{selectedScheduleProject?.clientEmail?.trim() || "No email on file"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-3.5 w-3.5" />
                      <span>
                        {normalizedSchedulePhone ||
                          selectedScheduleProject?.clientPhone?.trim() ||
                          "No phone on file"}
                      </span>
                    </div>
                  </div>
                </div>

                {scheduleAvailableDeliveryMethods.length ? (
                  <RadioGroup
                    value={scheduleDeliveryMethod}
                    onValueChange={(value) => setScheduleDeliveryMethod(value as ApprovalDeliveryMethod)}
                    className="grid gap-3 md:grid-cols-3"
                  >
                    {scheduleHasEmail ? (
                      <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-3">
                        <RadioGroupItem value="EMAIL" id="schedule-delivery-email" />
                        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-card-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          Email
                        </div>
                      </label>
                    ) : null}
                    {canUseScheduleSms ? (
                      <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-3">
                        <RadioGroupItem value="SMS" id="schedule-delivery-sms" />
                        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-card-foreground">
                          <Smartphone className="h-3.5 w-3.5" />
                          SMS
                        </div>
                      </label>
                    ) : null}
                    {scheduleHasEmail && canUseScheduleSms ? (
                      <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-3">
                        <RadioGroupItem
                          value="EMAIL_AND_SMS"
                          id="schedule-delivery-email-and-sms"
                        />
                        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-card-foreground">
                          <MessageSquare className="h-3.5 w-3.5" />
                          Email and SMS
                        </div>
                      </label>
                    ) : null}
                  </RadioGroup>
                ) : (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    No delivery channel is available for this client. Add an email address or a valid phone number first.
                  </div>
                )}

                {scheduleDeliveryUsesSms ? (
                  <SmsConsentConfirmation
                    checked={scheduleSmsConsentConfirmed}
                    onCheckedChange={(checked) => {
                      setScheduleSmsConsentConfirmed(checked);
                      if (checked) setScheduleSmsConsentError("");
                    }}
                    error={scheduleSmsConsentError}
                  />
                ) : null}
                {scheduleEmailReason ? <p className="text-xs text-muted-foreground">{scheduleEmailReason}</p> : null}
                {scheduleSmsReason ? <p className="text-xs text-muted-foreground">{scheduleSmsReason}</p> : null}
              </div>
            </div>
          </div>

          <SheetFooter className="gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setCreatePanelOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="outline"
              disabled={
                !selectedProjectId ||
                requiresScheduleAssignment ||
                !computedDurationMinutes ||
                computedDurationMinutes <= 0 ||
                scheduleAvailableDeliveryMethods.length === 0 ||
                savingSchedule
              }
              onClick={() => void saveSchedule(false)}
            >
              {savingSchedule ? t("common.saving") : t("schedule.modal.save")}
            </Button>
            <Button
              className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
              disabled={
                !selectedProjectId ||
                requiresScheduleAssignment ||
                !computedDurationMinutes ||
                computedDurationMinutes <= 0 ||
                (scheduleDeliveryUsesSms && !scheduleSmsConsentConfirmed) ||
                savingSchedule
              }
              onClick={() => void saveSchedule(true)}
            >
              <ChevronRight className="h-4 w-4" />
              {savingSchedule ? t("schedule.modal.sendingMessage") : t("schedule.modal.saveAndSend")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!cancelTargetBlock}
        onOpenChange={(open) => {
          if (!open) setCancelTargetBlock(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("schedule.cancelDialog.title", "Cancel this schedule block?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "schedule.cancelDialog.description",
                "This will mark the selected time block as canceled. You can still keep other scheduled times for this project.",
                { project: cancelTargetBlock?.projectName ?? "" },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updatingBlockId === cancelTargetBlock?.id}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={updatingBlockId === cancelTargetBlock?.id}
              onClick={() => void confirmCancelBlock()}
            >
              {updatingBlockId === cancelTargetBlock?.id
                ? t("common.saving")
                : t("schedule.detail.cancelBlock")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!inactiveProjectPrompt}
        onOpenChange={(open) => {
          if (!open && !reactivatingProject) {
            setInactiveProjectPrompt(null);
            setSelectedProjectId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("schedule.inactiveProjectDialog.title", "This project is not active")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "schedule.inactiveProjectDialog.description",
                "You can only assign time blocks to active projects. Do you want to move this project back to Active and continue?",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reactivatingProject}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={reactivatingProject}
              onClick={() => void activatePromptedProject()}
            >
              {reactivatingProject
                ? t("common.saving")
                : t("schedule.inactiveProjectDialog.activate", "Set project to Active")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!noteTargetBlock}
        onOpenChange={(open) => {
          if (!open && updatingBlockId !== noteTargetBlock?.id) {
            setNoteTargetBlock(null);
            setNoteDraft("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("schedule.addNoteDialog.title", "Add note")}</DialogTitle>
            <DialogDescription>
              {t(
                "schedule.addNoteDialog.description",
                "Add a closing note for this completed schedule block. This note will appear in the details panel.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              {noteTargetBlock?.projectName}
            </p>
            <Textarea
              rows={5}
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              placeholder={t("schedule.addNoteDialog.placeholder", "Add your note here...")}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={updatingBlockId === noteTargetBlock?.id}
              onClick={() => {
                setNoteTargetBlock(null);
                setNoteDraft("");
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              disabled={updatingBlockId === noteTargetBlock?.id}
              onClick={() => void saveBlockNote()}
            >
              {updatingBlockId === noteTargetBlock?.id ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
